import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader2, Paperclip, X, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { EMAIL_TEMPLATES, applyTemplateVars } from '../data/emailTemplates';

/**
 * Reusable dialog for sending one or more PDF/file attachments via IONOS SMTP.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - files: Array<{ filename: string, getBlob: () => Promise<Blob> } | { filename: string, blob: Blob }>
 *  - defaultSubject?: string
 *  - defaultMessage?: string
 *  - defaultRecipient?: string
 *  - templateVars?: { Kundenname?: string, Rechnungsnummer?: string, ... }
 *  - onSent?: () => void
 *
 * Sends multipart POST to /api/email/forward-files. Backend uses IONOS SMTP +
 * BCCs admin automatically.
 */
const SendByEmailDialog = ({
  open,
  onClose,
  files = [],
  defaultSubject = '',
  defaultMessage = '',
  defaultRecipient = '',
  templateVars = {},
  onSent,
}) => {
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState(files);
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    if (open) {
      setRecipient(defaultRecipient || '');
      setSubject(defaultSubject || '');
      setMessage(defaultMessage || '');
      setAttachments(files || []);
      setTemplateId('');
    }
  }, [open, defaultRecipient, defaultSubject, defaultMessage, files]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    if (!id) return;
    const tpl = EMAIL_TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    // Replace variable placeholders ({Kundenname}, {Rechnungsnummer}) with
    // actual values passed in via templateVars prop; leave unmatched ones
    // intact so the user can fill them in manually.
    setSubject(applyTemplateVars(tpl.subject, templateVars));
    setMessage(applyTemplateVars(tpl.message, templateVars));
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    const r = recipient.trim();
    if (!r || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r)) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    if (!subject.trim()) {
      toast.error('Bitte einen Betreff eingeben');
      return;
    }
    if (attachments.length === 0) {
      toast.error('Mindestens eine Datei zum Versenden ist erforderlich');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('recipient', r);
      formData.append('subject', subject.trim());
      formData.append('message', message);

      for (const att of attachments) {
        let blob = att.blob;
        if (!blob && typeof att.getBlob === 'function') {
          blob = await att.getBlob();
        }
        if (!blob) {
          throw new Error(`Anhang "${att.filename}" konnte nicht geladen werden`);
        }
        formData.append('files', blob, att.filename);
      }

      await axiosInstance.post('/email/forward-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });

      toast.success(`E-Mail mit ${attachments.length} Anhang${attachments.length === 1 ? '' : '/Anhängen'} versendet`);
      onSent?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Versand fehlgeschlagen';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="rounded-none border-[#D4C4B0] max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#5D4E3C]">
            <Mail className="h-5 w-5" /> Per E-Mail senden
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="se-template" className="text-[#5D4E3C] text-xs flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" /> Vorlage übernehmen (optional)
            </Label>
            <select
              id="se-template"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none"
              data-testid="se-template-select"
            >
              <option value="">— Eigene Nachricht schreiben —</option>
              {EMAIL_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#8B7355] mt-1">
              Du kannst Betreff &amp; Nachricht nach dem Übernehmen noch frei anpassen.
            </p>
          </div>

          <div>
            <Label htmlFor="se-recipient" className="text-[#5D4E3C] text-xs">Empfänger *</Label>
            <Input
              id="se-recipient"
              type="email"
              placeholder="kunde@beispiel.de"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="rounded-none border-[#D4C4B0] mt-1"
              data-testid="se-recipient-input"
            />
            <p className="text-[10px] text-[#8B7355] mt-1">Du bekommst automatisch eine Kopie (BCC).</p>
          </div>

          <div>
            <Label htmlFor="se-subject" className="text-[#5D4E3C] text-xs">Betreff *</Label>
            <Input
              id="se-subject"
              placeholder="z.B. Ihre Rechnung von Homeyoursoul"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-none border-[#D4C4B0] mt-1"
              data-testid="se-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="se-message" className="text-[#5D4E3C] text-xs">Nachricht</Label>
            <Textarea
              id="se-message"
              rows={5}
              placeholder="Sehr geehrte/r ..., anbei wie gewünscht ..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="rounded-none border-[#D4C4B0] mt-1"
              data-testid="se-message-input"
            />
          </div>

          <div>
            <Label className="text-[#5D4E3C] text-xs flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" /> Anhänge ({attachments.length})
            </Label>
            <div className="mt-1 border border-[#E8DCC8] bg-[#FAF5EE] p-2 max-h-32 overflow-y-auto">
              {attachments.length === 0 ? (
                <p className="text-xs text-[#8B7355] italic">Keine Anhänge</p>
              ) : (
                <ul className="space-y-1">
                  {attachments.map((att, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between gap-2 text-xs text-[#5D4E3C] bg-white px-2 py-1 border border-[#E8DCC8]"
                      data-testid={`se-attachment-${idx}`}
                    >
                      <span className="truncate">{att.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="text-[#8B7355] hover:text-red-600 flex-shrink-0"
                        aria-label={`Anhang ${att.filename} entfernen`}
                        disabled={sending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-[10px] text-[#8B7355] mt-1">Max. 10 Dateien · 15 MB pro Datei · 25 MB gesamt</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
            data-testid="se-cancel-btn"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || attachments.length === 0}
            className="rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white"
            data-testid="se-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {sending ? 'Wird gesendet…' : 'Senden'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendByEmailDialog;
